"use client";
import { useMemo } from "react";
import { useRouter } from "next/router";
import classNames from "classnames";
import { Bar } from "react-chartjs-2";
import { TypedChartComponent } from "react-chartjs-2/dist/types";
import {
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Chart as ChartJS,
} from "chart.js";

import { roundAndDropTrailingZeros } from "@ascendnow/shared";

import AscButton from "../AscButton/AscButton";
import {
  baselineAssessmentColor,
  getBaselineAssessmentCategories,
} from "./AscBaselineAssessmentComparison.helper";
import { AscBaselineAssessmentComparisonProps } from "./AscBaselineAssessmentComparison.type";
import { AscBaselineAssessmentComparisonHeader } from "./AscBaselineAssessmentComparison.Header";
import styles from "./AscBaselineAssessmentComparison.module.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export default function AscBaselineAssessmentComparison({
  title,
  subtitle,
  data,
  labels,
  categories,
  redirect,
  className,
  chartClassName,
}: AscBaselineAssessmentComparisonProps) {
  const router = useRouter();

  const categoryAttempts = useMemo(
    () => getBaselineAssessmentCategories(categories || []),
    [categories]
  );

  const chartData = useMemo(
    () =>
      data && Array.isArray(data) && data.length > 0
        ? {
            labels: labels,
            datasets: [
              {
                label:
                  categoryAttempts.find(
                    (el) => el.value === router.query.bAttempt1
                  )?.label || categoryAttempts[0].label,
                data: data[0],
                stack: "1",
                backgroundColor: baselineAssessmentColor.chart.blue,
              },
              {
                label:
                  categoryAttempts.find(
                    (el) => el.value === router.query.bAttempt2
                  )?.label || categoryAttempts[3].label,
                data: data[1],
                stack: "2",
                backgroundColor: baselineAssessmentColor.chart.violet,
              },
              {
                label:
                  categoryAttempts.find(
                    (el) => el.value === router.query.bAttempt3
                  )?.label || categoryAttempts[6].label,
                data: data[2],
                stack: "3",
                backgroundColor: baselineAssessmentColor.chart.gold,
              },
            ],
          }
        : { labels: [], datasets: [] },
    [
      data,
      labels,
      categoryAttempts,
      router.query.bAttempt1,
      router.query.bAttempt2,
      router.query.bAttempt3,
    ]
  );
  const options: Parameters<TypedChartComponent<"bar">>[0]["options"] = useMemo(
    () => ({
      type: "bar",
      responsive: true,
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";

              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += `${roundAndDropTrailingZeros(context.parsed.y)}%`;
              }
              return label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          border: {
            dash: [1, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            color: "#5E5E73",
            font: {
              family: "Poppins",
              size: 10,
              weight: "bolder",
              lineHeight: 4,
            },
          },
        },
        y: {
          stacked: true,
          border: {
            dash: [1, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            callback: function (value) {
              return `${value}%`;
            },
            color: "#9499B5",
            font: {
              family: "Poppins",
              size: 9,
              weight: "bolder",
              lineHeight: 4,
            },
          },
          max: 100,
        },
      },
      maintainAspectRatio: false,
      size: {
        width: "100%",
        height: "100%",
      },
    }),
    []
  );

  return (
    <div
      data-testid="asc-baseline-assessment-chart"
      className={classNames(styles.ground, className)}
    >
      <AscBaselineAssessmentComparisonHeader
        {...{ title, subtitle, categoryAttempts, categories }}
      />
      <div className={classNames(styles.chartContainer, chartClassName)}>
        <Bar options={options} data={chartData} />
      </div>
      {redirect && (
        <AscButton className={styles.redirectBtn} onClick={redirect?.onClick}>
          {redirect?.title}
        </AscButton>
      )}
    </div>
  );
}
